import {computed, effect, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {AppState} from "./app-state";
import {StorageService} from "./storage.service";
import {Product} from "../models/product";
import {QueryParamBootstrapperService, QueryParamData} from "./query-param-bootstrapper.service";
import {Contact} from "../models/contact";
import {mergeDefined, Utils} from "../../shared/utils/generics.utils";
import {PostcodeService} from "../../features/shared/postcode/postcode.service";
import {defaultIfEmpty, filter, firstValueFrom, Subject, take} from "rxjs";
import * as Sentry from "@sentry/angular";
import {Company} from "../models/company";
import {AnalysisCreationService} from "../../shared/services/analysis-creation.service";
import {Vehicle} from "../models/vehicle";
import {IndexedDBService} from "../../shared/services/indexed-db.service";
import {CarDriverPosition, CountrySettingsDto} from "../dto/country-settings-dto";
import {Scenario} from "../models/scenario";
import {StepDto} from "../dto/step-dto";
import {ConfigService} from "../config";
import {I18nService} from "../../shared/services/i18n.service";
import {getValidatedLicense} from "../../shared/utils/license.utils";
import {DialogOngoingAnalysisService} from "../../shared/services/dialog-ongoing-analysis.service";
import {AnalysisStatusEnum} from "../models/analysis-status";
import {Analysis} from "../models/analysis";
import {PhotoshootService} from "../../features/shared/photoshoot/services/photoshoot.service";
import {CookiesConsentService} from "../../shared/components/cookies-consent/services/cookies-consent.service";
import {MatomoTracker} from "ngx-matomo-client";
import {toObservable} from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: 'root'
})
export class AppStateService {

  private storage = inject(StorageService);
  private queryParamsService = inject(QueryParamBootstrapperService);
  private postcodeService = inject(PostcodeService);
  private analysisCreation = inject(AnalysisCreationService);
  private indexedDB = inject(IndexedDBService);
  private config = inject(ConfigService);
  private i18nService = inject(I18nService);
  private dialogService = inject(DialogOngoingAnalysisService);
  private photoshootService = inject(PhotoshootService);
  private cookies = inject(CookiesConsentService);
  private tracker = inject(MatomoTracker);

  private _state: WritableSignal<AppState> = signal({});

  readonly state = this._state.asReadonly();
  readonly product = computed(() => this._state().product ?? Product.UNKNOWN);
  readonly vehicle = computed(() => this._state().vehicle);
  readonly scenario = computed(() => this._state().scenario ?? Scenario.CAR_SCENARIO());
  readonly analysis = computed(() => this._state().analysis);
  readonly identificationScenario = computed(() => this._state().identificationScenario);
  readonly company = computed(() => this._state().company);
  readonly fleet = computed(() => this._state().fleet ?? 'public');
  readonly externalId = computed(() => this._state().externalId);
  readonly beta = computed(() => this._state().beta);
  readonly language = computed(() => this._state().language);
  readonly postcode = computed(() => this._state().postcode);
  readonly contact = computed(() => this._state().contact);
  readonly appointmentServiceForm = computed(() => this._state().appointmentServiceForm);
  readonly appointmentContactForm = computed(() => this._state().appointmentContactForm);
  readonly timeSlot = computed(() => this._state().timeSlot);
  readonly urlContext = computed(() => this._state().urlContext);
  readonly embedded = computed(() => (this._state().embedded || this.config.getWebapp().isEmbedded) ?? false);
  readonly redirectionURL = computed(() => this._state().redirectionURL);
  readonly analysisTiming = computed(() => this._state().analysisTiming);

  private lastStableProduct: Product | undefined;
  /** Prevents concurrent initialize() calls for the same product while one is already in-flight. */
  private _initializingForProduct: Product | null = null;
  private _isProductReady = signal(false);
  readonly isProductReady = this._isProductReady.asReadonly();

  private clearRequestedSubject = new Subject<void>();
  clearRequested$ = this.clearRequestedSubject.asObservable();

  constructor() {
    /**
     * Automatic persistence when state changes
     *
     * This effect watches for any change in `_state()`.
     * When something changes (like `vehicle`, `contact`, etc.),
     * it automatically saves the current state for the active product.
     *
     * Thanks to `onCleanup`, we ensure that outdated saves
     * are automatically canceled if the state changes again before the save happens.
     */
    effect(onCleanup => {

      if (!this.company()) return;

      const state = this._state();
      const product = state.product;

      // Ignore if no valid product yet
      if (!product || product === Product.UNKNOWN) return;

      // Prevent saving while switching product
      if (product !== this.lastStableProduct && this.lastStableProduct !== undefined) return;

      /**
       * The `cleanupFlag` is local to this single run of the effect.
       * When Angular re-runs this effect (because `_state()` changed),
       * it will call the cleanup function below, setting this flag to false.
       * That means any async or delayed work (like our microtask) will be canceled.
       */
      let active = true;
      // Register cleanup logic — automatically executed before the next run
      onCleanup(() => {
        active = false;
      });

      /**
       * `queueMicrotask()` schedules a task after all current microtasks complete,
       * ensuring that any other synchronous signal updates have finished.
       *
       * If the effect was re-run before this executes,
       * `cleanupFlag.active` will already be `false` → the save is skipped.
       */
      queueMicrotask(() => {
        if (active) {
          this.storage.saveProductState(product, state);
        }
      });
    });

    /**
     * Product switching logic
     *
     * This effect reacts ONLY when the `product` property of the state changes.
     * It saves the previous product’s data, then loads the new product’s data.
     *
     * The `onCleanup` callback ensures that if the product changes again very quickly,
     * any ongoing restore/save is automatically canceled before running a new one.
     */
    effect(onCleanup => {

      if (!this.company()) return;

      const newProduct = this.product();
      if (!newProduct || newProduct === this.lastStableProduct || [undefined, Product.UNKNOWN].includes(this.product())) return;

      // Guard: skip if initialize() is already in-flight for this product.
      // lastStableProduct is only updated after initialize() completes, so without this guard
      // a re-triggered effect (e.g. a second patch() from the router) would launch a second
      // concurrent initialize() — causing duplicate vehicle-creation requests.
      if (this._initializingForProduct === newProduct) {
        return;
      }

      this._isProductReady.set(false);

      const oldProduct = this.lastStableProduct;
      console.log(`[AppState] Product changed: ${oldProduct} → ${newProduct}`);

      /**
       * `onCleanup()` ensures that if this effect is re-run again (product changes twice in a row),
       * any async or pending logic from the previous run is cleaned up automatically.
       * This prevents overlapping state updates or double saves.
       */
      let active = true;
      onCleanup(() => (active = false));

      // Save previous product state before switching
      if (oldProduct && oldProduct !== Product.UNKNOWN) {
        const prevState = this._state();
        this.storage.saveProductState(oldProduct, prevState);
      }

      this._initializingForProduct = newProduct;

      // Restore new product state from storage
      (async () => {
        const restoredState = this.storage.toAppState(newProduct);
        const queryState = this.queryParamsService.readParams();

        if (active) {

          await this.initialize(this._state().company!, restoredState, queryState, newProduct);

          // Existing vehicle in storage continue analysis dialog.
          const analysisStatus = this._state().analysis?.status;
          if (this._state().vehicle?.id && !queryState.license && (!this._state().analysis || (analysisStatus && [AnalysisStatusEnum.STARTED, AnalysisStatusEnum.ABORTED].includes(analysisStatus)))) {
            this.dialogService.show();
          }
        }

        this._isProductReady.set(true);
        this.lastStableProduct = newProduct;
        this._initializingForProduct = null;
      })();
    });

    /**
     * Scenario recalculation
     *
     * This effect listens to both `product()` and `vehicle()`.
     * Every time one of them changes, it recomputes the Scenario.
     *
     * It runs independently of the product restore/save logic
     * to avoid conflicts and redundant updates.
     */
    effect((onCleanup) => {

      if (!this.company()) return;

      const product = this.product();
      const vehicle = this.vehicle();

      if (!product || product === Product.UNKNOWN) return;
      if (!vehicle) return; // If app state doesn't allow us to calculate scenario we don't

      // Flag for async cleanup (cancels outdated computations)
      let active = true;
      onCleanup(() => (active = false));

      // Async scenario calculation
      (async () => {
        try {
          const scenario = await this.getScenario(vehicle, product);
          if (active) {
            this._state.update((current) => ({
              ...current,
              scenario,
            }));
            console.log(`[AppState] Scenario recalculated for product=${product} & vehicle=${vehicle.license?.license}`);
          }
        } catch (error) {
          // Should not happen.
          Sentry.captureException(
            new Error(`[AppState] Failed to recalculate scenario for product=${product}`),
            {extra: {error}}
          );
        }
      })();
    });

    /**
     * Tracking / Cookies
     */
    effect(() => {
      this.setTrackers(this.vehicle()?.id);
    });

  }

  async initialize(company: Company, storageState: Partial<AppState>, queryState: QueryParamData, product: Product ) {
    const effectiveProduct = product ?? Product.UNKNOWN;
    const queryParamState = await this.initFromQueryParamData(queryState, company);

    // Clear data from previous appointment if one in storage is different
    this.handleAppointment(queryParamState, storageState);

    const merged = mergeDefined(storageState as AppState, queryParamState as AppState);
    merged.company = company;
    merged.product = product;

    if (effectiveProduct !== Product.UNKNOWN && (merged.vehicle?.id || merged.vehicle?.license)) {
      const localAnalysis = await this.handleLocalAnalysis(storageState, queryParamState);
      merged.analysis = await this.handleAnalysis(localAnalysis, merged);
      merged.scenario = await this.getScenario(merged.vehicle, this.product());
    } else {
      merged.analysis = undefined;
    }

    this._state.set(merged);
    console.log('[AppState] initialized', merged);
  }

  get snapshot(): AppState {
    return this._state();
  }

  patch(partial: Partial<AppState>) {
    console.log('[AppState] patch', partial);
    this._state.update(current => ({...current, ...partial}));
  }

  private async handleAnalysis(localAnalysis: Analysis | undefined, appState: AppState) {
    console.log('[AppState] handle analysis', localAnalysis, appState);
    const vehicle = appState.vehicle;
    if (!vehicle?.id && !vehicle?.license) {
      Sentry.captureException(new Error("Unable to get vehicle at init"));
      return undefined;
    }

    try {
      // The merged state is not applied yet at this point: pass it instead of the current one.
      const response = await firstValueFrom(this.analysisCreation.createOrResume$(appState).pipe(defaultIfEmpty(undefined)));
      if (!response) {
        return undefined;
      }
      const serverAnalysis = Analysis.create(response);
      appState.vehicle = {...vehicle, id: response.vehicle.id};

      // Ongoing analysis.
      if (serverAnalysis.status === AnalysisStatusEnum.UPLOADED) {
        return serverAnalysis;
      }

      const mergedPictures = await this.photoshootService.restoreFromAnalysis(serverAnalysis, localAnalysis?.id);

      console.log('[AppState] pictures', mergedPictures);
      return serverAnalysis;
    } catch (e) {
      Sentry.captureException(new Error("Unable to create or resume analysis at init"), {extra: {error: e}});
      await this.indexedDB.deleteAllPictures();
      return undefined;
    }
  }

  private async handleLocalAnalysis(storageState: Partial<AppState>, queryState: Partial<AppState>) {
    const hasQueryVehicle = !!queryState.vehicle;
    const isSameVehicle = storageState.vehicle?.license === queryState.vehicle?.license
      && storageState.vehicle?.id === queryState.vehicle?.id;

    const shouldKeepAnalysis = (): boolean => {
      if (!storageState.analysis) return false;
      if (!hasQueryVehicle) return true;
      return isSameVehicle;
    };

    if (!shouldKeepAnalysis()) {
      storageState.analysis = undefined;
      storageState.contact = undefined;
    }
    return storageState.analysis;
  }


  private async initFromQueryParamData(queryData: QueryParamData, company: Company) {
    const appData: AppState = {}
    appData.product = queryData.product;

    if (queryData.phone || queryData.email) {
      const contact = new Contact();
      if (queryData.phone && Utils.isValidPhoneNumber(queryData.phone)) {
        contact.phone = Utils.convertToE164(queryData.phone);
      }

      if (queryData.email && Utils.isValidEmail(queryData.email)) {
        contact.email = queryData.email;
      }
      appData.contact = contact.isValid() ? contact : undefined;
    }

    if (queryData.beta != null) {
      appData.beta = queryData.beta;
    }

    if (queryData.externalId) {
      appData.externalId = queryData.externalId;
    }

    if (queryData.lang) {
      appData.language = queryData.lang;
    }

    if (queryData.embedded) {
      appData.embedded = queryData.embedded;
    }

    if (queryData.redirectionURL) {
      appData.redirectionURL = queryData.redirectionURL;
    }

    if (queryData.urlContext) {
      appData.urlContext = queryData.urlContext;
    }

    if (queryData.postcode) {
      try {
        const postcodes = await firstValueFrom(this.postcodeService.getPostcode(queryData.postcode));
        if (postcodes.length >= 1) {
          appData.postcode = postcodes[0];
        }
      } catch (e) {
        Sentry.captureException(new Error("Unable to parse postcode: " + e), {extra: {error: e}});
      }
    }

    if (queryData.license) {
      const vehicle = await this.initVehicle(company, queryData.license, queryData.licenseCountryCode);
      if (vehicle) {
        vehicle.mileage = queryData.mileage
        appData.vehicle = vehicle;
      }
    }

    return appData;
  }

  async productReady() {
    return firstValueFrom(toObservable(this.isProductReady).pipe(filter(ready => ready), take(1)));
  }

  // Vehicle management //

  private initVehicle(company: Company, licensePlate: string, licenseCountryCode?: string): Promise<Vehicle | undefined> {
    const license = getValidatedLicense(company, licensePlate, licenseCountryCode);
    if (!license) return Promise.resolve(undefined);
    return Promise.resolve({license} as Vehicle);
  }

  // Scenario management //

  private async getScenario(vehicle: Vehicle, product: Product) {
    let scenario;
    const licenseCountry = vehicle?.license?.countryISO;

    const {carDriverPosition} = await (licenseCountry ?
        this.i18nService.getCountrySettings(licenseCountry) :
        Promise.resolve(CountrySettingsDto.DEFAULT)
    );
    if ([Product.SCANNER, Product.COLLECT].includes(product)) {
      scenario = this.buildScenario(product, carDriverPosition);
    } else {
      scenario = Scenario.CAR_SCENARIO(carDriverPosition);
    }
    return scenario;
  }

  private buildScenario(product: Product, carDriverPosition: CarDriverPosition) {
    let conf: { referenceTyresScenario: StepDto[], wearTyresScenario: StepDto[] };
    switch (product) {
      case Product.COLLECT:
        conf = this.config.getCollect();
        break;
      case Product.SCANNER:
        conf = this.config.getTyreScanner();
        break;
      default:
        conf = {referenceTyresScenario: [], wearTyresScenario: []};
    }
    return new Scenario.ScenarioBuilder()
      .setCarDriverPosition(carDriverPosition)
      .setStepsFromStepDto(conf.referenceTyresScenario, conf.wearTyresScenario)
      .build();
  }

  async clear(excludes?: (keyof AppState)[]) {
    this.storage.clear(this.product(), excludes);
    this.patch(this.storage.getProductState(this.product()));
    await this.indexedDB.deleteAllPictures();
    this.clearRequestedSubject.next();
    this.photoshootService.clearPictures();
  }

  async clearLocalPicturesAndAnalysis() {
    this.clearOnly(['analysis', 'analysisTiming']);
    await this.indexedDB.deleteAllPictures();
    this.photoshootService.clearPictures();
  }

  async clearLocalStorage() {
    this.storage.clear(this.product());
    await this.indexedDB.deleteAllPictures();
  }

  clearOnly(keys: (keyof AppState)[]) {
    this.storage.clearOnly(this.product(), keys);
    this.patch(this.storage.getProductState(this.product()));
  }

  // Cookies //

  private setTrackers(vehicleId?: number) {
    if (this.cookies.getCookiePreferences()?.performance.value && vehicleId) {
      Sentry.setTag("vehicle_id", vehicleId);
      Sentry.setUser({id: vehicleId.toString()});
      this.tracker.setUserId(vehicleId.toString());
    } else {
      Sentry.setTag("vehicle_id", undefined);
      Sentry.setUser(null);
      this.tracker.resetUserId();
    }
  }

  // Appointment //

  private handleAppointment(queryParamState: AppState, storageState: AppState) {
    if (this.product() === Product.APPOINTMENT) {
      if (queryParamState && queryParamState.urlContext !== storageState.urlContext) {
        this.clearOnly(['appointmentContactForm', 'appointmentServiceForm', 'timeSlot', 'urlContext']);
        storageState.appointmentServiceForm = undefined;
        storageState.appointmentContactForm = undefined;
        storageState.timeSlot = undefined;
      }
    }
  }
}
